﻿import Button from '@components/Button/Button';
import { ToastType } from '@components/Toast/Toast';
import {
  BULK_SHARED_CAPACITY_LIMIT,
  buildBulkSharedCapacityOutcome,
  buildBulkSharedCapacityRequests,
  buildEligibilityRows,
  bulkSharedCapacityToast,
  canSubmitBulkSharedCapacity,
  selectEligibleSharedCapacity,
  shouldRefreshAfterBulkSharedCapacity,
  withSharedCapacitySelection,
  type SharedCapacityPhase,
} from '@store/loadboard/bulkSharedCapacity';
import {
  bulkAddSharedCapacity,
  checkBulkSharedCapacityEligibility,
} from '@store/loadboard/bulkSharedCapacityApi';
import { setLoadBoardStore } from '@store/loadboard/store';
import type {
  BulkSharedCapacityEligibilityResult,
  BulkSharedCapacityRequest,
  BulkSharedCapacityResult,
  ILoadBoardViewModel,
} from '@store/loadboard/types';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { handleToast } from '@utils/utils';
import { Show, createEffect, createMemo, createSignal } from 'solid-js';

import {
  MassSharedCapacityScrollList,
  type ShareCapacityRowTone,
} from './MassSharedCapacityScrollList';
import { ShareCapacityResultReview } from './MassSharedCapacitySections';

type Props = {
  id: string;
  loads: ILoadBoardViewModel[];
  onClose: (id: string) => void;
  refreshGrid?: () => void | Promise<void>;
};

type EligibilityState = 'idle' | 'checking' | 'done' | 'failed';

const runBulkSharedCapacitySubmit = async (input: {
  items: BulkSharedCapacityRequest[];
  refreshGrid?: () => void | Promise<void>;
  onResult: (result: BulkSharedCapacityResult) => void;
  onFailure: () => void;
}) => {
  setLoadBoardStore('overlay', true);
  try {
    const result = await bulkAddSharedCapacity(input.items);
    const assignedCount = input.items.filter(
      (item) => !result.failedLoadIds.includes(item.loadId),
    ).length;
    input.onResult(result);
    const toast = bulkSharedCapacityToast(result, assignedCount);
    if (toast.type === 'success' && toast.message !== undefined) {
      handleToast(ToastType.Success, toast.message);
    }
    if (shouldRefreshAfterBulkSharedCapacity(result)) {
      setLoadBoardStore('overlay', false);
      await input.refreshGrid?.();
    }
  } catch (error: unknown) {
    input.onFailure();
    handleToast(
      ToastType.Error,
      error instanceof Error ? error.message : 'Failed to add shared capacity',
    );
  } finally {
    setLoadBoardStore('overlay', false);
  }
};

export const MassSharedCapacityModal = (props: Props) => {
  const [phase, setPhase] = createSignal<SharedCapacityPhase>('browse');
  const [selected, setSelected] = createSignal<number[]>([]);
  const [eligibilityState, setEligibilityState] =
    createSignal<EligibilityState>('idle');
  const [eligibility, setEligibility] = createSignal<
    BulkSharedCapacityEligibilityResult | undefined
  >();
  const [bulkResult, setBulkResult] = createSignal<
    BulkSharedCapacityResult | undefined
  >();

  createEffect(() => {
    const loadIds = props.loads.map((load) => load.loadId);
    if (loadIds.length === 0) {
      return;
    }

    setEligibilityState('checking');
    void (async () => {
      try {
        const result = await checkBulkSharedCapacityEligibility(loadIds);
        setEligibility(result);
        setEligibilityState('done');
      } catch (error: unknown) {
        setEligibilityState('failed');
        handleToast(
          ToastType.Error,
          error instanceof Error
            ? error.message
            : 'Failed to check shared capacity eligibility',
        );
      }
    })();
  });

  const eligibilityView = createMemo(() => {
    const result = eligibility();
    if (result === undefined) {
      return undefined;
    }

    return buildEligibilityRows(props.loads, result);
  });

  const ineligibleMessage = createMemo(() => {
    const rows = eligibilityView()?.ineligible ?? [];
    return new Map(rows.map((row) => [row.loadId, row.message]));
  });

  const eligibleLoadIds = createMemo(
    () => eligibilityView()?.eligible.map((load) => load.loadId) ?? [],
  );

  const allEligibleSelected = createMemo(() => {
    const eligible = eligibleLoadIds();
    if (eligible.length === 0) {
      return false;
    }

    const chosen = new Set(selected());
    return eligible.every((loadId) => chosen.has(loadId));
  });

  const toneFor = (loadId: number): ShareCapacityRowTone => {
    if (eligibilityState() === 'failed') {
      return 'failed';
    }

    if (eligibilityState() === 'checking') {
      return 'checking';
    }

    if (ineligibleMessage().has(loadId)) {
      return 'ineligible';
    }

    if (eligibleLoadIds().includes(loadId)) {
      return 'eligible';
    }

    return 'pending';
  };

  const detailFor = (loadId: number) => {
    switch (toneFor(loadId)) {
      case 'failed':
        return 'Eligibility could not be checked.';
      case 'checking':
        return 'Checking…';
      case 'ineligible':
        return ineligibleMessage().get(loadId) ?? 'This load cannot be shared.';
      case 'eligible':
        return 'Eligible';
      case 'pending':
        return 'No eligibility result returned.';
    }
  };

  const toneRank: Record<ShareCapacityRowTone, number> = {
    eligible: 0,
    checking: 1,
    pending: 2,
    failed: 3,
    ineligible: 4,
  };

  const sortedLoads = createMemo(() =>
    props.loads
      .map((load, index) => ({
        load,
        index,
        rank: toneRank[toneFor(load.loadId)],
      }))
      .sort((a, b) => a.rank - b.rank || a.index - b.index)
      .map((entry) => entry.load),
  );

  const canSelect = (loadId: number) => {
    if (!eligibleLoadIds().includes(loadId) || phase() !== 'browse') {
      return false;
    }

    return (
      selected().includes(loadId) ||
      selected().length < BULK_SHARED_CAPACITY_LIMIT
    );
  };

  const outcome = createMemo(() => {
    const result = bulkResult();
    if (result === undefined) {
      return undefined;
    }

    const submitted = buildBulkSharedCapacityRequests(props.loads, selected());
    return buildBulkSharedCapacityOutcome(
      props.loads,
      submitted.map((item) => item.loadId),
      result,
    );
  });

  const submitDisabled = () => {
    if (phase() === 'result') {
      return false;
    }

    return !canSubmitBulkSharedCapacity({
      phase: phase(),
      selectedCount: selected().length,
    });
  };

  const onSubmit = () => {
    if (phase() === 'result') {
      props.onClose(props.id);
      return;
    }

    if (submitDisabled() || phase() === 'submitting') {
      return;
    }

    const items = buildBulkSharedCapacityRequests(props.loads, selected());
    if (items.length === 0) {
      return;
    }

    setPhase('submitting');
    void runBulkSharedCapacitySubmit({
      items,
      refreshGrid: props.refreshGrid,
      onResult: (result) => {
        setBulkResult(result);
        setPhase('result');
      },
      onFailure: () => {
        setPhase('browse');
      },
    });
  };

  return (
    <>
      <div class="flex max-h-[70vh] min-h-0 flex-col gap-3">
        <Show when={phase() !== 'result'}>
          <MassSharedCapacityScrollList
            loads={sortedLoads()}
            checking={eligibilityState() === 'checking'}
            selectedLoadIds={selected()}
            eligibleLoadIds={eligibleLoadIds()}
            allEligibleSelected={allEligibleSelected()}
            ineligibleCount={eligibilityView()?.ineligible.length ?? 0}
            detailFor={detailFor}
            toneFor={toneFor}
            canSelect={canSelect}
            onToggle={(loadId, checked) => {
              setSelected((current) =>
                withSharedCapacitySelection(current, loadId, checked),
              );
            }}
            onSelectAll={(checked) => {
              setSelected(
                checked
                  ? selectEligibleSharedCapacity(
                      props.loads.map((load) => load.loadId),
                      eligibleLoadIds(),
                    )
                  : [],
              );
            }}
          />
        </Show>
        <Show when={phase() === 'result'}>
          <ShareCapacityResultReview
            result={bulkResult()}
            outcome={outcome()}
          />
        </Show>
      </div>
      <div class="relative mt-2 flex w-full items-center justify-end gap-2 pr-2">
        <div class="absolute left-4 text-sm text-armstrong-primary">
          Selected loads: {selected().length} / {BULK_SHARED_CAPACITY_LIMIT}
        </div>
        <Button
          isLoading={phase() === 'submitting'}
          disabled={submitDisabled()}
          variant="contained"
          label={phase() === 'result' ? 'Close' : 'Share'}
          type="button"
          onClick={onSubmit}
          dataTestId={formatDataTestId(
            'Modal Add Shared Capacity Submit',
            'button',
          )}
        />
        <Show when={!bulkResult()?.success}>
          <Button
            label="Cancel"
            type="button"
            variant="outlined"
            onClick={() => {
              props.onClose(props.id);
            }}
            dataTestId={formatDataTestId(
              'Modal Add Shared Capacity Cancel',
              'button',
            )}
          />
        </Show>
      </div>
    </>
  );
};
